import React from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { RTL } from '../../../cache/CacheRtl';
import TranslationDialogButton from '../../translation/TranslationDialogButton';

/**
 * BasicInfoSection — the exercise's CANONICAL name, plus the translation editor.
 *
 * Previously two side-by-side TextFields, `nameHeb` and `name`, i.e. a bilingual convention
 * hardcoded into the form: adding Arabic would have meant a third field here, a third column on
 * `exercises`, a DTO change and an OpenAPI regen in both clients.
 *
 * Now there is exactly ONE editable name — the canonical (Hebrew) one, which the server mirrors
 * into the entity's derived column so search, sort and the `existsByName*` uniqueness checks keep
 * working. Every OTHER language, English included, is edited through `TranslationDialogButton`,
 * which renders itself from `GET /admin/translation/schema/EXERCISE`. Enabling a tenth locale
 * changes nothing in this file.
 *
 * The English name field is deliberately gone rather than kept alongside: two editors writing the
 * same text to two different stores (the legacy `exercises.name` column vs. an `entity_translation`
 * row at locale `en`) is how they drift, and the panel is the one that the mobile read path
 * actually resolves from. The legacy column keeps whatever English it already held until the
 * Phase-3 migration drops it; see `useExerciseForm.handleSave`.
 */
export interface ExerciseBasicInfoValues {
    id?: number | null;
    name?: string;
    nameHeb?: string;
}

export interface BasicInfoSectionProps {
    formValues: ExerciseBasicInfoValues;
    setFieldValue: (field: 'nameHeb', value: string) => void;
    setFieldTouched: (field: string) => void;
    errors?: Record<string, string | undefined>;
    touched?: Record<string, boolean>;
    nameHebExistsError?: string | null;
    isCheckingNameHeb?: boolean;
    checkNameHebExists?: (value: string) => void;
    /** 'dialog' | 'page' — retained for call-site parity; the layout no longer differs. */
    mode?: string;
}

/** field key -> Hebrew label. Per FIELD, not per locale — adding a locale needs no entry here. */
const EXERCISE_FIELD_LABELS: Record<string, string> = {
    name: 'שם התרגיל',
    description: 'תיאור',
    // No `weightInfo` entry. It is the note shown BESIDE the weight input telling the trainee
    // which number to type (the whole bar, the plates only, one dumbbell, one side) — and it is a
    // property of the resistance type, not of the exercise: 733 exercises share just four values,
    // one per resistance type. It now renders from the progress-static catalogue as
    // `weight_info_<RESISTANCE_TYPE>` and is no longer a translatable field, so /schema stops
    // returning it and no label is needed.
};

const inputStyles = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        '& fieldset': { borderColor: '#E2E8F0' },
        '&:hover fieldset': { borderColor: '#CBD5E1' },
        '&.Mui-focused fieldset': { borderColor: '#57837E' },
        '&.Mui-error fieldset': { borderColor: '#EF4444' },
    },
};

const BasicInfoSection = ({
    formValues,
    setFieldValue,
    setFieldTouched,
    errors = {},
    touched = {},
    nameHebExistsError,
    isCheckingNameHeb,
    checkNameHebExists,
}: BasicInfoSectionProps) => {
    const nameHebError = nameHebExistsError || (touched.nameHeb ? errors.nameHeb : undefined);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EditIcon sx={{ color: '#64748B', fontSize: '20px' }} />
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '16px',
                            color: '#1E293B',
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                        }}
                    >
                        מידע בסיסי
                    </Typography>
                </Box>

                <TranslationDialogButton
                    type="EXERCISE"
                    entityId={formValues.id ?? null}
                    fieldLabels={EXERCISE_FIELD_LABELS}
                    entityNoun="התרגיל"
                    dialogTitle="תרגומי התרגיל"
                />
            </Box>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                    <RTL>
                        <TextField
                            fullWidth
                            label="שם התרגיל (עברית) *"
                            value={formValues.nameHeb || ''}
                            onChange={(e) => setFieldValue('nameHeb', e.target.value)}
                            onBlur={(e) => {
                                setFieldTouched('nameHeb');
                                checkNameHebExists?.(e.target.value);
                            }}
                            error={Boolean(nameHebError)}
                            helperText={
                                nameHebError ||
                                (isCheckingNameHeb
                                    ? 'בודק...'
                                    : 'שמות בשפות אחרות נערכים בחלון התרגומים')
                            }
                            sx={inputStyles}
                        />
                    </RTL>
                </Grid>
            </Grid>
        </Box>
    );
};

export default BasicInfoSection;
